/**
 * Feature Flags Configuration
 *
 * Controls gradual rollout of unified socket adapter pattern.
 * Allows toggling between legacy direct socket usage and new adapter layer.
 *
 * Part of Phase 2: Shared Interface & Adapter Pattern
 */

// ============================================
// Feature Flag Types
// ============================================

export interface SocketFeatureFlags {
  /**
   * Use unified adapter layer instead of direct socket access.
   * When false, existing happySocket/useSelfHostedSocket code paths are used.
   * When true, the new adapter pattern handles all socket operations.
   */
  useUnifiedAdapter: boolean;

  /**
   * Enable event normalization for all incoming socket events.
   * Transforms CLI Dashboard / Vibe Remote formats into unified format.
   */
  enableEventNormalization: boolean;

  /**
   * Enable connection mutex to prevent simultaneous cloud + self-hosted.
   * Phase 1 feature that should remain enabled.
   */
  enableConnectionMutex: boolean;

  /**
   * Enable event idempotency tracking to block duplicate events.
   * Phase 1 feature that should remain enabled.
   */
  enableEventIdempotency: boolean;

  /**
   * Enable session join guards to prevent double-joins.
   * Phase 1 feature that should remain enabled.
   */
  enableSessionJoinGuards: boolean;

  /**
   * Log all normalized events for debugging.
   * Should be disabled in production for performance.
   */
  debugLogNormalizedEvents: boolean;

  /**
   * Validate all incoming events against expected schema.
   * Useful for catching server-side changes early.
   */
  validateIncomingEvents: boolean;

  /**
   * Preferred socket mode.
   */
  preferredMode: 'cloud' | 'self-hosted';

  /**
   * Auto-reconnect on connection loss.
   */
  enableAutoReconnect: boolean;

  /**
   * Maximum reconnection attempts before giving up.
   */
  maxReconnectAttempts: number;

  /**
   * Use streaming message assembly (combine chunks into full messages).
   */
  enableStreamAssembly: boolean;
}

// ============================================
// Default Configuration
// ============================================

/**
 * Default feature flags - conservative settings for production.
 * Phase 1 features enabled, Phase 2 adapter disabled until tested.
 */
export const DEFAULT_FEATURE_FLAGS: SocketFeatureFlags = {
  // Phase 2 features (disabled by default until fully tested)
  useUnifiedAdapter: false,
  enableEventNormalization: false,

  // Phase 1 features (enabled - already tested)
  enableConnectionMutex: true,
  enableEventIdempotency: true,
  enableSessionJoinGuards: true,

  // Debugging (disabled in production)
  debugLogNormalizedEvents: false,
  validateIncomingEvents: false,

  // Connection preferences
  preferredMode: 'self-hosted',
  enableAutoReconnect: true,
  maxReconnectAttempts: 5,

  // Message handling
  enableStreamAssembly: true,
};

/**
 * Development feature flags - more verbose logging, validation enabled.
 */
export const DEV_FEATURE_FLAGS: SocketFeatureFlags = {
  ...DEFAULT_FEATURE_FLAGS,
  debugLogNormalizedEvents: true,
  validateIncomingEvents: true,
};

/**
 * Testing feature flags - full adapter enabled for integration tests.
 */
export const TEST_FEATURE_FLAGS: SocketFeatureFlags = {
  ...DEFAULT_FEATURE_FLAGS,
  useUnifiedAdapter: true,
  enableEventNormalization: true,
  debugLogNormalizedEvents: true,
  validateIncomingEvents: true,
};

// ============================================
// Runtime Flag Management
// ============================================

let currentFlags: SocketFeatureFlags = { ...DEFAULT_FEATURE_FLAGS };

/**
 * Get current feature flags.
 */
export function getFeatureFlags(): Readonly<SocketFeatureFlags> {
  return currentFlags;
}

/**
 * Update feature flags at runtime.
 * Merges provided flags with current flags.
 *
 * @param updates - Partial flag updates
 */
export function setFeatureFlags(updates: Partial<SocketFeatureFlags>): void {
  currentFlags = { ...currentFlags, ...updates };
  console.log('[FeatureFlags] Updated flags:', currentFlags);
}

/**
 * Reset feature flags to defaults.
 */
export function resetFeatureFlags(): void {
  currentFlags = { ...DEFAULT_FEATURE_FLAGS };
  console.log('[FeatureFlags] Reset to defaults');
}

/**
 * Initialize feature flags based on environment.
 * Call this at app startup.
 */
export function initializeFeatureFlags(): void {
  const isDev = import.meta.env.DEV;
  const isTest = import.meta.env.MODE === 'test';

  if (isTest) {
    currentFlags = { ...TEST_FEATURE_FLAGS };
    console.log('[FeatureFlags] Initialized with TEST flags');
  } else if (isDev) {
    currentFlags = { ...DEV_FEATURE_FLAGS };
    console.log('[FeatureFlags] Initialized with DEV flags');
  } else {
    currentFlags = { ...DEFAULT_FEATURE_FLAGS };
    console.log('[FeatureFlags] Initialized with PRODUCTION flags');
  }

  // Check for URL overrides (dev only)
  if (isDev && typeof window !== 'undefined') {
    applyUrlOverrides();
  }
}

/**
 * Apply URL query parameter overrides for testing.
 * Example: ?ff_useUnifiedAdapter=true&ff_debugLogNormalizedEvents=true
 */
function applyUrlOverrides(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const overrides: Partial<SocketFeatureFlags> = {};

    // Boolean flags
    const booleanFlags: (keyof SocketFeatureFlags)[] = [
      'useUnifiedAdapter',
      'enableEventNormalization',
      'enableConnectionMutex',
      'enableEventIdempotency',
      'enableSessionJoinGuards',
      'debugLogNormalizedEvents',
      'validateIncomingEvents',
      'enableAutoReconnect',
      'enableStreamAssembly',
    ];

    for (const flag of booleanFlags) {
      const value = params.get(`ff_${flag}`);
      if (value !== null) {
        (overrides as Record<string, unknown>)[flag] = value === 'true';
      }
    }

    // String flags
    const preferredMode = params.get('ff_preferredMode');
    if (preferredMode === 'cloud' || preferredMode === 'self-hosted') {
      overrides.preferredMode = preferredMode;
    }

    // Number flags
    const maxReconnect = params.get('ff_maxReconnectAttempts');
    if (maxReconnect !== null) {
      const num = parseInt(maxReconnect, 10);
      if (!isNaN(num) && num >= 0) {
        overrides.maxReconnectAttempts = num;
      }
    }

    if (Object.keys(overrides).length > 0) {
      setFeatureFlags(overrides);
      console.log('[FeatureFlags] Applied URL overrides:', overrides);
    }
  } catch (e) {
    console.warn('[FeatureFlags] Failed to apply URL overrides:', e);
  }
}

// ============================================
// Convenience Checkers
// ============================================

/**
 * Check if unified adapter should be used.
 */
export function shouldUseUnifiedAdapter(): boolean {
  return currentFlags.useUnifiedAdapter;
}

/**
 * Check if event normalization is enabled.
 */
export function shouldNormalizeEvents(): boolean {
  return currentFlags.enableEventNormalization;
}

/**
 * Check if connection mutex is enabled.
 */
export function isConnectionMutexEnabled(): boolean {
  return currentFlags.enableConnectionMutex;
}

/**
 * Check if event idempotency is enabled.
 */
export function isEventIdempotencyEnabled(): boolean {
  return currentFlags.enableEventIdempotency;
}

/**
 * Check if session join guards are enabled.
 */
export function isSessionJoinGuardsEnabled(): boolean {
  return currentFlags.enableSessionJoinGuards;
}

/**
 * Check if debug logging for normalized events is enabled.
 */
export function shouldLogNormalizedEvents(): boolean {
  return currentFlags.debugLogNormalizedEvents;
}

/**
 * Get preferred socket mode.
 */
export function getPreferredMode(): 'cloud' | 'self-hosted' {
  return currentFlags.preferredMode;
}
